import { color, icon, testColors, type TestTone } from "@mesketch/design-tokens";
import type { ReactElement } from "react";

// Ikony testů v3 (CR-022, DES-003 §5): část portrétu, kterou test přidává (CR-021): tvar za
// hlavou s postavou, květ z hrudi, hvězdy, kopce s cestou. Vystřižené plné tvary jako koláž,
// barevná (hotový test), skica (nezačatý), zjednodušená pro 32 a 20 px. Rozpracovaný test
// kombinuje skicu a barvu podle podílu: barva stoupá zdola (zadání 8.1). Odstíny jsou tokeny
// `icon-*` z palety koláže.

export type IconVariant = "color" | "sketch" | "mini";

const SKETCH = { fill: "none", stroke: color.graphite, strokeLinejoin: "round", strokeLinecap: "round" } as const;

// Jaký jsem? (vrstva base): vystřižený volný tvar za hlavou a postava.
const BLOB = "M6.5 27 C3.5 16.5 9.5 5 21 4.2 C32.5 3.5 38 13.5 35.2 24 C33.5 30.5 30 33.5 24 34.5 C15.5 36 8.6 34 6.5 27 Z";
const BLOB_INNER = "M11 24.5 C9.5 16.5 13.5 9 21 8.6 C28.5 8.2 32 14.5 30.4 21.6 C29.4 26 26.5 28.4 21.8 29 C16 29.8 12 28.8 11 24.5 Z";
const HEAD = { cx: 20, cy: 16.8, rx: 5.4, ry: 6.2 } as const;
const BUST = "M16.7 21.5 C16.9 24 16 25.2 13.6 26 C9.7 27.2 8 30.6 8 37 H32 C32 30.6 30.3 27.2 26.4 26 C24 25.2 23.1 24 23.3 21.5 Z";

// Jak pracuju s emocemi? (vrstva inner): květ z hrudi, pět lístků a střed.
const PETAL = { cx: 20, cy: 11, rx: 5.3, ry: 8.2 } as const;
const PETALS = [0, 72, 144, 216, 288] as const;

// V čem jsem silný? (vrstva sky): hvězdy s prohnutými paprsky.
function sparkle(cx: number, cy: number, r: number): string {
  const k = r * 0.2;
  return `M${cx} ${cy - r} Q${cx + k} ${cy - k} ${cx + r} ${cy} Q${cx + k} ${cy + k} ${cx} ${cy + r} Q${cx - k} ${cy + k} ${cx - r} ${cy} Q${cx - k} ${cy - k} ${cx} ${cy - r} Z`;
}
const SPARKS = [sparkle(15.5, 24, 12), sparkle(29, 11.5, 7.2), sparkle(8.5, 8.5, 4.2)] as const;

// Kde se najdu v práci? (vrstva land): dva kopce a cesta k horizontu se zlatým kamenem.
const HILL_BACK_TOP = "M1 24 C8 15.5 16 15.5 23 20 C28 16.5 34 16 39 18.5";
const HILL_FRONT_TOP = "M1 28.5 C11 23 26 24.5 39 30";
const HILL_BACK = `${HILL_BACK_TOP} V37 H1 Z`;
const HILL_FRONT = `${HILL_FRONT_TOP} V37 H1 Z`;
const PATH = "M14 37 C17 32 24 30.5 22.2 26.5 C21.2 24.2 23.2 22.2 25.2 21 L26.2 21.8 C24.6 23 24 24.6 24.8 26.4 C26.8 31 21.5 33.5 22.6 37 Z";

function who(mini: boolean) {
  return (
    <>
      <path d={BLOB} fill={icon.backdrop} />
      {mini ? null : <path d={BLOB_INNER} fill={icon.backdropLight} />}
      <ellipse {...HEAD} fill={icon.figure} />
      <path d={BUST} fill={icon.figure} />
    </>
  );
}

function emotion() {
  return (
    <>
      {PETALS.map((a) => (
        <ellipse key={a} {...PETAL} transform={`rotate(${a} 20 20)`} fill={a === 0 ? icon.petalLight : icon.petal} />
      ))}
      <circle cx="20" cy="20" r="4.6" fill={icon.petalCenter} />
    </>
  );
}

function strength(mini: boolean) {
  return (
    <>
      <path d={SPARKS[0]} fill={icon.starLight} />
      <path d={SPARKS[1]} fill={icon.starDeep} />
      {mini ? null : <path d={SPARKS[2]} fill={icon.starDeep} />}
    </>
  );
}

function work(mini: boolean) {
  return (
    <>
      <path d={HILL_BACK} fill={icon.hillLight} />
      <path d={HILL_FRONT} fill={icon.hillDeep} />
      <path d={PATH} fill={icon.path} />
      {mini ? null : <circle cx="25.8" cy="20.4" r="1.9" fill={icon.stone} />}
    </>
  );
}

const COLOR: Record<TestTone, (mini: boolean) => ReactElement> = { who, emotion, strength, work };

const SKETCHES: Record<TestTone, ReactElement> = {
  who: (
    <g {...SKETCH}>
      <path d={BLOB} strokeWidth="1.1" />
      <path d={BLOB_INNER} strokeWidth="0.7" opacity="0.6" />
      <ellipse {...HEAD} strokeWidth="1.2" />
      <path d={BUST} strokeWidth="1.2" />
    </g>
  ),
  emotion: (
    <g {...SKETCH}>
      {PETALS.map((a) => (
        <ellipse key={a} {...PETAL} transform={`rotate(${a} 20 20)`} strokeWidth="1.1" />
      ))}
      <circle cx="20" cy="20" r="4.6" strokeWidth="1.1" />
    </g>
  ),
  strength: (
    <g {...SKETCH}>
      {SPARKS.map((d) => (
        <path key={d} d={d} strokeWidth="1.2" />
      ))}
    </g>
  ),
  work: (
    <g {...SKETCH}>
      <path d={HILL_BACK_TOP} strokeWidth="1.1" />
      <path d={HILL_FRONT_TOP} strokeWidth="1.1" />
      <path d={PATH} strokeWidth="0.9" />
      <circle cx="25.8" cy="20.4" r="1.9" strokeWidth="0.9" />
    </g>
  ),
};

/**
 * Ikona testu. `progress` (0 až 1) u varianty `sketch` dobarví ikonu zdola podle podílu
 * dokončení (rozpracovaný test, zadání 8.1). `label` z ní udělá obrázek pro čtečku.
 */
export function TestIcon({ tone, variant = "color", size = 40, progress = 0, label }: { tone: TestTone; variant?: IconVariant; size?: number; progress?: number; label?: string }) {
  const p = Math.max(0, Math.min(1, progress));
  const clip = `mk-progress-${tone}-${Math.round(p * 100)}`;
  const a11y = label === undefined ? { "aria-hidden": true as const } : { role: "img", "aria-label": label };
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" data-variant={variant} data-tone={tone} {...a11y}>
      {variant === "sketch" ? SKETCHES[tone] : COLOR[tone](variant === "mini")}
      {variant === "sketch" && p > 0 ? (
        <>
          <clipPath id={clip}>
            <rect x="0" y={40 * (1 - p)} width="40" height={40 * p} />
          </clipPath>
          <g clipPath={`url(#${clip})`}>{COLOR[tone](false)}</g>
        </>
      ) : null}
    </svg>
  );
}

export const TEST_TONES: readonly TestTone[] = Object.keys(testColors) as TestTone[];

// Hotový test (REQ-041, Libor 21. 10. 2026): ilustrovaná fajfka jako samolepka v rohu karty, dva
// vystřižené zelené tvary jako koláž ikon a přes ně tah štětcem, který vybíhá vpravo nahoru.
// Dekorace, stav nese text „hotovo“.
const DONE_BACK = "M10.4 28.6 C8.9 17.8 16.3 9.9 26.9 10.4 C37.1 10.9 43 18.2 42 28.4 C41.1 37.1 34.2 42.9 25.6 42.4 C16.9 41.9 11.2 36.8 10.4 28.6 Z";
const DONE_FRONT = "M6.4 25.4 C5.3 15.5 12.4 7.6 22.6 8 C32.5 8.4 38.6 15.6 37.6 25.3 C36.7 33.9 29.9 39.8 21.4 39.3 C12.8 38.8 7.2 33.6 6.4 25.4 Z";
const DONE_CHECK =
  "M10.2 24.6 C11.6 22.9 14.1 23 15.5 24.7 L19.6 29.6 C25.5 20.5 33.5 11.2 44.2 4.2 C45 3.7 45.8 4.6 45.2 5.3 C36.8 14.2 29.2 24.6 23.4 35.6 C22.3 37.7 19.4 38 17.9 36.1 L10.4 28.2 C9.4 27.1 9.3 25.7 10.2 24.6 Z";

export function DoneMark({ size = 56 }: { size?: number }): ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" focusable="false" className="done-mark">
      <path d={DONE_BACK} fill={icon.doneMid} />
      <path d={DONE_FRONT} fill={icon.doneLight} />
      <path d={DONE_CHECK} fill={icon.done} />
    </svg>
  );
}
